import { Component, OnDestroy, OnInit } from '@angular/core';
import { WebAuthenticatorService } from '../../services/auth/web-authenticator.service';
import { EventTransportService } from '../../services/event-transport/event-transport.service';
import { Subscription } from 'rxjs';
import { MfaService } from '../../services/mfa/mfa.service';
import { eAuthResult, eMfaTransactionStartV2Result, eStartAuthResult } from '@common/request_enums';
import { IActionInfo } from '@common/action-interfaces';
import { AppKeyV2Service } from 'src/app/services/app-password/app-key-v2.service';
import { SessionAuthService } from 'src/app/services/auth/session-auth.service';
import { HyperIDAuthenticatorService } from 'src/app/services/mfa/hyperid-authenticator-app.service';

@Component({
    selector: 'app-user-verification',
    templateUrl: './user-verification.component.html',
    styleUrls: [ './user-verification.component.scss' ]
})
export class UserVerificationComponent implements OnInit, OnDestroy
{

    public transactionId: number = 0;
    public actionCode: string;
    public visibility: boolean = false;
    public processing: boolean = false;
    public mfaAvailability: boolean = false;
    public hyperIdAuthenticatorAppUrl?: string;

    private actionInfo: IActionInfo;
    private eventSubscription: Subscription;

    constructor(
        private sessionAuthService: SessionAuthService,
        private webAuthenticator: WebAuthenticatorService,
        private eventTransport: EventTransportService,
        private passwordService: AppKeyV2Service,
        private mfaService: MfaService,
        private readonly mfaAppService: HyperIDAuthenticatorService,
    )
    {
    }

    async ngOnInit()
    {
        this.eventSubscription = this.eventTransport.on('verificate-user').subscribe(async (args) =>
        {
            this.verificate(args[ 'data' ]);
        });
    }

    ngOnDestroy()
    {
        if (Boolean(this.eventSubscription))
        {
            this.eventSubscription.unsubscribe();
        }
    }

    public async startVerificate(): Promise<boolean>
    {
        try
        {
            this.processing = true;

            const isWebAuthnSupported = await this.webAuthenticator.isWebAuthenticationSupported();
            if (isWebAuthnSupported)
            {
                this.actionInfo[ 'isWebAuthnSupported' ] = true;
            }

            const { start_auth_result, transaction } = await this.sessionAuthService.startVerificate(this.actionInfo);
            if (start_auth_result == eStartAuthResult.not_required)
            {
                this.verificationSuccess();
                return false;
            }

            if (start_auth_result == eStartAuthResult.mfa)
            {
                if (!transaction || !transaction.transaction_id) return true;
            }

            this.show();
            this.transactionId = transaction.transaction_id;
            this.actionCode = transaction.code;
            this.hyperIdAuthenticatorAppUrl = transaction.hyper_id_authenticator_app_url;

            this.mfaAppService.tryOpenApp(this.hyperIdAuthenticatorAppUrl);

            let verificationResult = eAuthResult.not_completed;
            switch (start_auth_result)
            {
                case eStartAuthResult.mfa:
                    this.mfaAvailability = true;
                    const result = this.actionInfo ?
                        await this.mfaService.verificateFor(this.actionInfo) :
                        await this.mfaService.verificateFor();
                    verificationResult = result.authResult;
                    this.transactionId = result.transactionId;
                    break;

                case eStartAuthResult.webauthn:
                    this.mfaAvailability = false;
                    if (this.actionInfo)
                        verificationResult = await this.webAuthenticator.authenticateUser(this.actionInfo);
                    else
                        verificationResult = await this.webAuthenticator.authenticateUser();
                    break;
            }

            if (verificationResult == eAuthResult.accept)
            {
                this.verificationSuccess();
                return false;
            }
            if ([ eAuthResult.reject, eAuthResult.not_completed ].includes(verificationResult))
            {
                this.verificationFailed();
                return false;
            }

            return true;
        }
        catch (error)
        {
            console.error(error);
            this.verificationFailed();
        }
        finally
        {
            this.processing = false;
        }
    }

    public onBack()
    {
        if (this.actionInfo)
        {
            if (this.mfaAvailability)
                this.mfaService.stopVerification(this.actionCode);
            else
            {
                this.passwordService.cancelFor(this.actionInfo);
                this.webAuthenticator.skipAuthentication();
            }
        }
        this.verificationFailed();
    }

    private async verificate(data?)
    {
        if (data)
        {
            const verificateData = data[ 'data' ];
            if (verificateData)
            {
                const result = <eMfaTransactionStartV2Result>verificateData[ 'result' ];
                if (result == eMfaTransactionStartV2Result.success)
                {
                    this.continueVerificate(data);
                    return;
                }
            }
            this.actionInfo = <IActionInfo>data;
        }

        if (this.actionInfo)
        {
            const isProcessing = await this.startVerificate();
            if (isProcessing)
                this.show();
        }
    }

    private async continueVerificate(data)
    {
        let responce;
        let ott_hexed: string;

        try
        {
            this.processing = true;

            this.transactionId = Number(data[ 'data' ][ 'transaction_id' ]);
            this.actionCode = data[ 'data' ][ 'code' ];
            this.actionInfo = <IActionInfo>{ template_id: data[ 'template_id' ] };
            this.hyperIdAuthenticatorAppUrl = data[ 'data' ][ 'hyper_id_authenticator_app_url' ];
            const actionId = data[ 'action_id' ];
            if (actionId)
            {
                this.actionInfo.action_id = actionId;
            }

            const result = <eStartAuthResult>data[ 'start_auth_result' ];
            if (result != eStartAuthResult.ott_hexed)
            {
                this.mfaAvailability = result == eStartAuthResult.mfa;
                this.show();
                if (this.mfaAvailability)
                {
                    this.mfaAppService.tryOpenApp(this.hyperIdAuthenticatorAppUrl);
                    responce = await this.mfaService.waitForOtt(this.actionInfo);
                }
                else
                {
                    responce = await this.webAuthenticator.authenticateUser2Ott(this.actionInfo);
                }

                const authResult = <eAuthResult>responce[ 'transaction_complete_result' ];
                if (typeof authResult !== 'undefined' && authResult == eAuthResult.reject)
                {
                    throw new Error('ORDER-REJECTED');
                }

                ott_hexed = responce[ 'ott_hexed' ];
            }

            if (ott_hexed && ott_hexed.length > 0)
            {
                return this.verificationSuccess(ott_hexed);
            }

            this.verificationFailed();
        } catch (error)
        {
            return this.verificationFailed(error.message);
        }
        finally
        {
            this.processing = false;
        }
    }

    private verificationSuccess(ott_hexed?)
    {
        const successData = { result: 'SUCCESS' };

        if (ott_hexed)
        {
            successData[ 'ott_hexed' ] = ott_hexed;
        }

        this.hide();
        this.eventTransport.send('user-verification', successData);
    }

    private verificationFailed(code?)
    {
        const failedData = { result: 'FAILED' };

        if (code)
        {
            failedData[ 'code' ] = code;
        }

        this.hide();
        this.eventTransport.send('user-verification', failedData);
        this.eventTransport.send('toast', { message: 'VERIFICATION.NOT-VERIFIED', type: 'error' });
    }

    private show()
    {
        this.visibility = true;
    }

    private hide()
    {
        this.visibility = false;
        this.processing = false;
    }
}
